// AdminUserManagement.jsx - Kullanıcı yönetimi paneli
import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import { getAllUsers, blockUser, unblockUser, updateUserRole } from '../../utils/api';

const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Kullanıcıları yükle
  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getAllUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
      setMessage({ type: 'error', text: 'Kullanıcılar yüklenirken hata: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filtreleme
  useEffect(() => {
    let filtered = [...users];

    // Arama
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filtresi
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => 
        roleFilter === 'admin' ? user.admin : !user.admin
      );
    }

    // Durum filtresi
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? !user.blocked : user.blocked
      );
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, statusFilter, users]);

  // Admin yetkisi ver/kaldır
  const handleToggleAdmin = async (userId, currentAdmin) => {
    const action = currentAdmin ? 'kaldırmak' : 'vermek';
    if (!window.confirm(`Bu kullanıcının admin yetkisini ${action} istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setProcessing(userId);
      await updateUserRole(userId, !currentAdmin);
      setMessage({ 
        type: 'success', 
        text: `Admin yetkisi başarıyla ${currentAdmin ? 'kaldırıldı' : 'verildi'}` 
      });
      await loadUsers();
    } catch (error) {
      console.error('Admin yetkisi güncellenemedi:', error);
      setMessage({ type: 'error', text: 'İşlem başarısız: ' + (error.message || 'Bilinmeyen hata') });
    } finally {
      setProcessing(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  // Kullanıcı engelle/aktifleştir
  const handleToggleBlock = async (userId, currentBlocked) => {
    const action = currentBlocked ? 'aktifleştirmek' : 'engellemek';
    if (!window.confirm(`Bu kullanıcıyı ${action} istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setProcessing(userId);
      if (currentBlocked) {
        await unblockUser(userId);
      } else {
        await blockUser(userId);
      }
      setMessage({ 
        type: 'success', 
        text: `Kullanıcı başarıyla ${currentBlocked ? 'aktifleştirildi' : 'engellendi'}` 
      });
      await loadUsers();
    } catch (error) {
      console.error('Kullanıcı durumu değiştirilemedi:', error);
      setMessage({ type: 'error', text: 'İşlem başarısız: ' + error.message });
    } finally {
      setProcessing(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  // Kullanıcı detayları göster
  const showUserDetails = (user) => {
    setSelectedUser(user);
  };

  // Badge renkleri
  const getRoleBadge = (isAdmin) => {
    return isAdmin ? (
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-600 text-white">
        Admin
      </span>
    ) : (
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white">
        Kullanıcı
      </span>
    );
  };

  const getStatusBadge = (isBlocked) => {
    return isBlocked ? (
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-600 text-white">
        Engellenmiş
      </span>
    ) : (
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">
        Aktif
      </span>
    );
  };

  if (loading) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Kullanıcılar yükleniyor...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center">
            <svg className="w-8 h-8 text-indigo-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Kullanıcı Yönetimi
          </h2>
          <p className="text-gray-400">Tüm kullanıcıları görüntüleyin ve yönetin</p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-900 border border-green-600 text-green-200' :
            'bg-red-900 border border-red-600 text-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Arama */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Ara</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="İsim veya email..."
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Rol filtresi */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Rol</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tümü</option>
              <option value="admin">Admin</option>
              <option value="user">Kullanıcı</option>
            </select>
          </div>

          {/* Durum filtresi */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Durum</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tümü</option>
              <option value="active">Aktif</option>
              <option value="blocked">Engellenmiş</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-900 bg-opacity-30 p-4 rounded-lg border border-blue-700">
            <div className="text-2xl font-bold text-blue-200">{users.length}</div>
            <div className="text-sm text-blue-400">Toplam Kullanıcı</div>
          </div>
          <div className="bg-purple-900 bg-opacity-30 p-4 rounded-lg border border-purple-700">
            <div className="text-2xl font-bold text-purple-200">
              {users.filter(u => u.admin).length}
            </div>
            <div className="text-sm text-purple-400">Admin</div>
          </div>
          <div className="bg-green-900 bg-opacity-30 p-4 rounded-lg border border-green-700">
            <div className="text-2xl font-bold text-green-200">
              {users.filter(u => !u.blocked).length}
            </div>
            <div className="text-sm text-green-400">Aktif</div>
          </div>
          <div className="bg-red-900 bg-opacity-30 p-4 rounded-lg border border-red-700">
            <div className="text-2xl font-bold text-red-200">
              {users.filter(u => u.blocked).length}
            </div>
            <div className="text-sm text-red-400">Engellenmiş</div>
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Kayıt Tarihi
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {filteredUsers.map((user) => (
                <tr key={user.uid} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-100">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {getRoleBadge(user.admin)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {getStatusBadge(user.blocked)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-300">
                    {user.createdAt?.toDate 
                      ? user.createdAt.toDate().toLocaleDateString('tr-TR') 
                      : user.createdAt 
                      ? new Date(user.createdAt).toLocaleDateString('tr-TR') 
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => showUserDetails(user)}
                        className="text-blue-400 hover:text-blue-300"
                        title="Detaylar"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleToggleAdmin(user.uid, user.admin)}
                        disabled={processing === user.uid}
                        className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-semibold disabled:opacity-50 ${
                          user.admin
                            ? 'bg-purple-700 hover:bg-purple-600 text-white'
                            : 'bg-gray-600 hover:bg-purple-700 text-gray-200 hover:text-white'
                        }`}
                        title={user.admin ? 'Admin Yetkisi Kaldır' : 'Admin Yetkisi Ver'}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        {user.admin ? 'Admin Kaldır' : 'Admin Yap'}
                      </button>
                      <button
                        onClick={() => handleToggleBlock(user.uid, user.blocked)}
                        disabled={processing === user.uid}
                        className={`${user.blocked ? 'text-green-400 hover:text-green-300' : 'text-red-400 hover:text-red-300'} disabled:opacity-50`}
                        title={user.blocked ? 'Aktifleştir' : 'Engelle'}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {user.blocked ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          )}
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p className="text-gray-400">Kullanıcı bulunamadı</p>
          </div>
        )}
      </Card>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-white">Kullanıcı Detayları</h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center mb-6">
                  <div className="flex-shrink-0 h-20 w-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                    {selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div className="ml-6">
                    <h4 className="text-xl font-semibold text-white">{selectedUser.name || 'İsimsiz'}</h4>
                    <p className="text-gray-400">{selectedUser.email}</p>
                    <div className="flex gap-2 mt-2">
                      {getRoleBadge(selectedUser.admin)}
                      {getStatusBadge(selectedUser.blocked)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-sm text-gray-400">Kayıt Tarihi</div>
                    <div className="text-white font-medium">
                      {selectedUser.createdAt?.toDate 
                        ? selectedUser.createdAt.toDate().toLocaleDateString('tr-TR') 
                        : selectedUser.createdAt 
                        ? new Date(selectedUser.createdAt).toLocaleDateString('tr-TR') 
                        : '-'}
                    </div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-sm text-gray-400">UID</div>
                    <div className="text-white font-mono text-xs break-all">{selectedUser.uid}</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-sm text-gray-400">Toplam Rezervasyon</div>
                    <div className="text-white font-medium">{selectedUser.totalReservations || 0}</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-sm text-gray-400">Aktif Rezervasyon</div>
                    <div className="text-white font-medium">{selectedUser.activeReservations || 0}</div>
                  </div>
                </div>

                {selectedUser.penalties && selectedUser.penalties > 0 && (
                  <div className="bg-red-900 bg-opacity-30 border border-red-700 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-6 h-6 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <div className="text-red-200 font-semibold">Ceza Durumu</div>
                        <div className="text-red-300 text-sm">{selectedUser.penalties} ceza puanı</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  onClick={() => setSelectedUser(null)}
                  variant="secondary"
                >
                  Kapat
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
